import React, { useCallback } from "react";
import { signOut } from "aws-amplify/auth";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import IconChevronLeft from "../../dynamicicons/icon-chevron-left";
import IconChevronRight from "../../dynamicicons/icon-chevron-right";
import IconVelavuAlerts from "../../dynamicicons/icon-velavu-alerts";
import IconVelavuAnalytics from "../../dynamicicons/icon-velavu-analytics";
import IconVelavuAssets from "../../dynamicicons/icon-velavu-assets";
import IconVelavuDevices from "../../dynamicicons/icon-velavu-devices";
import IconVelavuMap from "../../dynamicicons/icon-velavu-map";
import AlertCounter from "../../elements/alert-counter";
import VelavuAvatar from "../../elements/velavu-avatar";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuAlertModal from "../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import VelavuButton from "../../elements/velavu-button";
import whitelabel from "../../whitelabel";
import {
	useHasPermission,
	useSignInContext,
} from "../sign-in-context/sign-in-context";
import SidebarIcon from "./sidebar-icon";
import * as styles from "./sidebar.module.scss";
import SidebarUserMenu from "./sidebar-user-menu";
import IconRefresh from "../../dynamicicons/icon-refresh";
import SidebarNavItem from "./sidebar-nav-item";
import { makeStyleable } from "../../helper/icon-helper";
import IconStar from "../../dynamicicons/icon-star";
import IconInventoryOutlined from "../../dynamicicons/icon-inventory-outlined";
import { classArr } from "../../helper/style-helper";

interface SidebarProps {
	isExpanded: boolean;
	setExpanded: (updater: (isExpanded: boolean) => boolean) => void;
	isRefreshing: boolean;

	showAlerts: boolean;
	setShowAlerts: (showAlerts: boolean) => void;
	alertCount: number;
	onOpenSettings: VoidFunction;
	onRefreshContent: VoidFunction;
}

function NavItems(props: { isExpanded: boolean }) {
	const { t } = useTranslation();
	const userPreferences = useSignInContext().preferences;

	const canReadRules = useHasPermission("rules:read");

	return (
		<>
			<SidebarNavItem
				route="/map"
				icon={makeStyleable(IconVelavuMap)}
				label={t("sidebar.nav.map")}
				expanded={props.isExpanded}
			/>
			<SidebarNavItem
				route="/assets"
				icon={makeStyleable(IconVelavuAssets)}
				label={t("sidebar.nav.assets")}
				expanded={props.isExpanded}
			/>
			<SidebarNavItem
				route="/devices"
				icon={makeStyleable(IconVelavuDevices)}
				label={t("sidebar.nav.devices")}
				expanded={props.isExpanded}
			/>
			<SidebarNavItem
				route="/analytics"
				icon={makeStyleable(IconVelavuAnalytics)}
				label={t("sidebar.nav.analytics")}
				expanded={props.isExpanded}
				beta
			/>
			{userPreferences.showRulesEngine && canReadRules && (
				<SidebarNavItem
					route="/rules"
					icon={makeStyleable(IconStar)}
					label={t("sidebar.nav.rules")}
					expanded={props.isExpanded}
					beta
				/>
			)}
			{process.env.FEATURE_FLAGS.enableInventory && (
				<SidebarNavItem
					route="/inventory"
					icon={makeStyleable(IconInventoryOutlined)}
					label={t("sidebar.nav.inventory")}
					expanded={props.isExpanded}
					beta
				/>
			)}
		</>
	);
}

export default function Sidebar(props: SidebarProps) {
	const { t } = useTranslation();
	const location = useLocation();
	const userContext = useSignInContext();
	const userName = userContext.velavuUser?.name ?? "";
	const userOrganization =
		userContext.organization?.name ?? t("common.organization");
	const userImage = userContext.velavuUser?.profile_img_url;
	const propsSetExpanded = props.setExpanded;
	const toggleCollapse = useCallback(
		() => propsSetExpanded((isExpanded) => !isExpanded),
		[propsSetExpanded],
	);

	const { pushModal } = useVelavuModal();

	const propsOnOpenSettings = props.onOpenSettings;
	const openSettings = useCallback(() => {
		propsOnOpenSettings();
	}, [propsOnOpenSettings]);

	const openLogOut = useCallback(() => {
		pushModal<boolean>((resolve) => (
			<VelavuAlertModal>
				<VelavuAlertModalTitle>
					{t("sidebar.logout_modal.title", {
						brand: whitelabel.name,
					})}
				</VelavuAlertModalTitle>

				<VelavuAlertModalContent>
					{t("sidebar.logout_modal.content")}
				</VelavuAlertModalContent>

				<VelavuAlertModalActions>
					<VelavuButton
						label={t("sidebar.logout_modal.cancel")}
						variant="outlined"
						fullWidth
						onClick={() => resolve(false)}
					/>

					<VelavuButton
						label={t("sidebar.logout_modal.log_out")}
						variant="normal"
						fullWidth
						onClick={() => resolve(true)}
					/>
				</VelavuAlertModalActions>
			</VelavuAlertModal>
		)).then((result) => {
			if (!result) return;

			signOut().catch(console.log);
		});
	}, [pushModal, t]);

	const redirectToSupport = useCallback(() => {
		window.open(whitelabel.supportURL);
	}, [whitelabel.supportURL]);

	return (
		<div
			className={classArr(
				styles.container,
				(location.pathname === "/map" ||
					location.pathname.startsWith("/map/")) &&
					!props.showAlerts &&
					styles.elevated,
				props.isExpanded ? styles.expanded : styles.collapsed,
				props.showAlerts && styles.attached,
			)}
		>
			<div className={styles.header}>
				{props.isExpanded ? (
					<img
						src={whitelabel.logoWide}
						style={{ width: 100 }}
						alt={whitelabel.name}
					/>
				) : (
					<img
						src={whitelabel.logoSquare}
						alt={whitelabel.name}
						width="40px"
						height="40px"
					/>
				)}
			</div>

			<NavItems isExpanded={props.isExpanded} />

			<div className={styles.spacer} />

			<SidebarIcon
				className={styles.iconButtonSpacing}
				tooltip={
					<span className="p2m">{t("sidebar.tooltips.refresh")}</span>
				}
				onClick={props.onRefreshContent}
			>
				<IconRefresh
					className={
						props.isRefreshing ? styles.spinAnimation : undefined
					}
					color={whitelabel.palette.secondary700}
				/>
			</SidebarIcon>

			<SidebarIcon
				className={styles.iconButtonSpacing}
				tooltip={
					<>
						<span className="p2m">
							{t("sidebar.tooltips.alerts")}
						</span>
						{props.alertCount > 0 && (
							<AlertCounter
								style={{ marginLeft: 12 }}
								count={props.alertCount}
							/>
						)}
					</>
				}
				onClick={() => props.setShowAlerts(!props.showAlerts)}
				badge={props.alertCount > 0}
			>
				<IconVelavuAlerts color={whitelabel.palette.secondary700} />
			</SidebarIcon>
			<SidebarIcon
				className={styles.iconButtonSpacing}
				tooltip={
					<span className="p2m">
						{props.isExpanded
							? t("sidebar.tooltips.collapse")
							: t("sidebar.tooltips.expand")}
					</span>
				}
				onClick={toggleCollapse}
				clickCausesLayout
			>
				{props.isExpanded ? (
					<IconChevronLeft color={whitelabel.palette.secondary700} />
				) : (
					<IconChevronRight color={whitelabel.palette.secondary700} />
				)}
			</SidebarIcon>
			<SidebarIcon
				className={styles.iconButtonSpacing}
				tooltip={
					<SidebarUserMenu
						name={userName}
						organization={userOrganization}
						onOpenSettings={openSettings}
						onOpenSupport={redirectToSupport}
						onLogOut={openLogOut}
					/>
				}
				tooltipAlignment="bottom"
				tooltipVerticalNudge={10}
			>
				<VelavuAvatar name={userName} image={userImage} />
			</SidebarIcon>
		</div>
	);
}
